<template>
    <BasePanel 
        headline="lang_GENERIC_CAPTION_OTHER_IDS"
        :alvqa="alvqa"
        :helpContext="getHelpContext"
    >
        <template v-slot:panelContent>
            <v-data-table
                :headers="headers"
                :items="filteredItems"
                hide-default-footer
                :header-props="{ sortIcon: '' }"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
                :items-per-page="itemsPerPage"
                :page.sync="currentPage"
            >
            <!-- TOP SLOT -->
                <template v-slot:top>
                    <v-row no-gutters class="px-4 pb-6" align="center">
                        <v-col cols="4">
                            <SearchFilter
                                context="WorksOtherIds"
                                :alvqa="alvqa + '-textfield-search'"
                            />
                        </v-col>
                        <v-spacer></v-spacer>
                        <v-col cols="3">
                            <BaseButton 
                                @click="addNewRow"
                                :disabled="$route.params.alvId === 'new'"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                </template>

            <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

            <!-- Items -->
            <template v-slot:item.sourceAlvId="{ item }">
                <div :alvqa="alvqa + '-list-row-text-source-alv-id'">
                    {{ getSourceName(item.sourceAlvId) }}
                </div>
            </template>
                <template v-slot:[`item.otherId`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-other-id'">
                        {{ item.otherId }}
                    </div>
                </template>
                <template v-slot:item.country="{ item }">
                    <BaseTooltip>
                        <template v-slot:text>
                        {{ item.country }}
                        </template>
                        <template v-slot:content>
                            {{ $alvPlugin.getTranslatedCountryName(item.country, true) }}
                        </template>
                    </BaseTooltip>
                </template>
            <!-- Buttons -->
                <template v-slot:[`item.delete`]="{ item }">
                    <BaseIcon 
                        @click="deleteRow(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + 'list-row-icon-delete'"
                    />
                </template>
                <template v-slot:[`item.inlineEdit`]="{ item }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item)"
                    />
                </template>
            </v-data-table>
            <v-pagination
                v-if="numberOfPages > 1"
                total-visible="10"
                v-model="currentPage"
                :length="numberOfPages"
                color="pagination"
            ></v-pagination>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseTooltip from '@/components/_base/BaseTooltip'
import SearchFilter from '@/components/_filters/SearchFilter'

export default {
    name: "OtherIdsEditPanel",
    components: {
        BasePanel,
        BaseButton,
        BaseIcon,
        BaseTooltip,
        SearchFilter,
    },
    data() {
        return {
            alvqa: 'publishing-works-edit-form-panel-other-ids',
            currentPage: 1
        }
    },
    computed: {
        sourceOptions() {
            return this.$store.getters.getAzForOtherIdsSource
        },
        headers() {
            return [
                {
                    text: this.$t("lang_GENERIC_CAPTION_SOURCE"), 
                    align: "left", 
                    value: "sourceAlvId",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-source'
                },
                {
                    text: this.$t("lang_PUBLISHING_WORKS_CAPTION_WORK_ID"), 
                    align: "left", 
                    value: "otherId",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-other-id'
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_COUNTRY"), 
                    align: "left", 
                    value: "country",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-country'
                },
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    width: "70px",
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    class: 'text-no-wrap'
                }
            ]
        },
        listItems() { return this.$store.getters.getWorksOtherIds },
        search() { return this.$store.getters.getWorksOtherIdsSearch },
        filteredItems() {
            const term = (this.search || '').toString().trim().toLowerCase()
            if (!term) return this.listItems
            return this.listItems.filter(item => {
                const otherId = (item.otherId || '').toString().toLowerCase()
                const country = (item.country || '').toString().toLowerCase()
                const source = this.getSourceName(item.sourceAlvId).toLowerCase()
                return otherId.includes(term) || country.includes(term) || source.includes(term)
            })
        },
        itemsPerPage() { return this.$store.getters.getItemsPerPage },
        numberOfPages() { return Math.ceil(this.filteredItems.length / this.itemsPerPage) },
        getHelpContext() {
            return 'PublishingWorksEditPanelOtherIDs'
        }
    },
    watch: {
        search() { this.currentPage = 1 },
        numberOfPages(pages) {
            if (this.currentPage > pages) this.currentPage = pages > 0 ? pages : 1
        }
    },
    methods: {
        getSourceName(alvId) {
            if (!alvId || !this.sourceOptions) return ''
            const found = this.sourceOptions.find(o => o.value === alvId)
            return found ? found.text + " ("+alvId+")" : ''
        },
        addNewRow(){
            this.$store.commit('setShowWorksOtherIdsEditingDialog', true)
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))

            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultWorksOtherIds)) 
            this.$store.commit('setWorksOtherIdsEditingDialogDetails', defaultValues)
        },

        deleteRow(item) {
            // set the selected row that should be deleted from the delete action 
            // and open the deletion dialog
            this.$store.commit("setSelectedWorksOtherIds", item)
            this.$store.commit('setDeletionDialogContext', 'SelectedWorksOtherIds')
            this.$store.commit('setShowDeletionDialog', true)
        },

        editInlineFunction(item) {
            this.$store.commit('setSelectedWorksOtherIds', item.id)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))

            // set the copy in dialog details to edit
            this.$store.commit('setWorksOtherIdsEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) // set headline
            this.$store.commit('setShowWorksOtherIdsEditingDialog', true) 
        }
    },
    async mounted() {
        this.$store.commit('setWorksOtherIdsSearch', '')
        this.$store.commit('setCountries')
        this.$store.dispatch("fetchAzForOtherIdsSource")
    },
    
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
        table-layout: fixed;
    }
</style>